import * as THREE from 'three';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import type { Lesson, PanoVariants } from '@vrlearning/lesson-schema';
import type { BuiltScene } from '../scene.js';
import { PanoView, cubeTexture, preparePano, rendererGpu, type PreparedPano } from '../pano.js';
import { rendererCaps } from '../pano-caps.js';
import type { PanoSource } from '../pano-source.js';
import { buildStudio } from '../studio.js';
import { buildWorld, type BuiltWorld } from '../worlds/build.js';
import { hdriFile, type Manifest } from '../worlds/manifest.js';
import type { Fetchers, WorldDefinition } from '../worlds/world.js';
import { HaloField, lessonHalos } from './halos.js';
import {
  ModelLight,
  ROOM_RADIANCE,
  modelLightNeed,
  skyRadiance,
  type WorldLight,
} from './model-light.js';
import { gradientSky, lightTexture, skyInWorker, type SkyParts } from './sky.js';

/**
 * The look of a lesson (ADR-032, "The look, for every lesson"):
 *
 * | | World | 360° photo | Studio | Solar system | AR |
 * | Soft directional shadow | yes | from the photo's bright region | yes (the key light) | no | no |
 * | Environment light (PMREM) | the world's HDRI | the photo (1024 × 512) | RoomEnvironment | no | no |
 * | Fog, water, drifting sky, particles | per world | no | no | starfield as is | no |
 * | Glow halos | yes | yes | yes | yes | yes |
 * | Model light, when dark (ADR-100) | yes | no | no | no | no |
 *
 * `flat` is the look before worlds (`?look=flat`): none of it.
 */

/** What the look needs of the stage: its scene and camera, and the renderer for PMREM. */
export type LookTarget = {
  readonly scene: THREE.Scene;
  readonly camera: THREE.PerspectiveCamera;
  readonly renderer?: THREE.WebGLRenderer;
  activeCamera(): THREE.Camera;
  redrawShadow(): void;
};

export type Look = {
  /** Settles when the lesson may be shown: its world or first photo in, or 15 s gone. */
  readonly ready: Promise<void>;
  readonly world?: BuiltWorld;
  /** A 360° photo lesson's photos (ADR-036). */
  readonly pano?: PanoView;
  /** A VR world lesson's model light (ADR-100): the model runtime attaches its models to it. */
  readonly modelLight?: ModelLight;
  /** The world's light now, as the model light reads it; undefined without a world. */
  worldLight(): WorldLight | undefined;
  /** The world's files that could not be had. */
  missing(): number;
  /** At the black of a step's fade: late files go in, and the shadow is drawn again. */
  atStep(): void;
  /**
   * `held` while motion is held at a fade: the world's features wait too. `black` while the view is
   * fully black (a teleport's middle, the lesson's start): the model light is set there at once.
   */
  update(dt: number, elapsed: number, moving: boolean, held?: boolean, black?: boolean): void;
  /** The world's features' states at a step: eased there when `animate`, else at once. */
  setWorldStates(states: ReadonlyMap<string, string>, animate: boolean): void;
  dispose(): void;
};

/** A world's reach: the volcano stands at 260 m, the sea runs to the horizon. */
export const WORLD_FAR = 2000;
/** The studio's sky light from a room made in code, strength beside its key light. */
export const STUDIO_ENVIRONMENT = 0.5;

const rooms = new WeakMap<THREE.WebGLRenderer, THREE.Texture>();

/** The studio's environment: three's RoomEnvironment through PMREM, made once per renderer. */
function roomEnvironment(renderer: THREE.WebGLRenderer): THREE.Texture {
  let texture = rooms.get(renderer);
  if (!texture) {
    const pmrem = new THREE.PMREMGenerator(renderer);
    const room = new RoomEnvironment();
    texture = pmrem.fromScene(room, 0.04).texture;
    room.traverse((node) => {
      const mesh = node as THREE.Mesh<THREE.BufferGeometry, THREE.Material>;
      mesh.geometry?.dispose();
      mesh.material?.dispose();
    });
    pmrem.dispose();
    rooms.set(renderer, texture);
  }
  return texture;
}

/**
 * Fog in the horizon's colour as the sky shows it (the import measured it as
 * displayed): three converts a fog colour to the output's space itself.
 */
export function fogColour(hex: string): THREE.Color {
  return new THREE.Color(hex);
}

/** What a 360° photo lesson's look needs: the signed URLs, and a loader tests replace. */
export type PanoLook = {
  media: Readonly<Record<string, string>>;
  /** The photos' signed cubes and light copies (ADR-041); `{}` or absent without. */
  variants?: Readonly<PanoVariants>;
  prepare?: (source: PanoSource) => Promise<PreparedPano>;
  /** The desktop note when the studio stands in for a photo; `undefined` once a photo shows. */
  onNote?: (text: string | undefined) => void;
};

export function applyLook(
  target: LookTarget,
  lesson: Lesson,
  built: BuiltScene,
  options: {
    flat?: boolean;
    world?: WorldDefinition;
    /** The world's files and where they are: a served package's own (W11); the code worlds' by default. */
    manifest?: Manifest;
    urlOf?: (stored: string) => string;
    fetchers?: Fetchers;
    pano?: PanoLook;
  } = {},
): Look {
  const flat = options.flat === true;
  const isAr = lesson.mode === 'ar';
  const { scene, renderer } = target;

  const halos = flat
    ? undefined
    : new HaloField(
        lessonHalos(lesson, new Map([...built.objects].map(([id, object]) => [id, object.body]))),
      );
  if (halos) built.root.add(halos.mesh);

  let world: BuiltWorld | undefined;
  let modelLight: ModelLight | undefined;
  /** The sky could not be had: the world is lit by the studio's room (fallbackSky). */
  let skyFellBack = false;
  let environment: THREE.WebGLRenderTarget | undefined;
  let background: THREE.Texture | undefined;
  const definition = options.world;

  if (!flat && !isAr && lesson.scene.type === 'studio' && renderer) {
    scene.environment = roomEnvironment(renderer);
    scene.environmentIntensity = STUDIO_ENVIRONMENT;
  }

  if (!flat && definition) {
    const sky = hdriFile(definition.sky.hdri, options.manifest);
    const fallbackSky = (): void => {
      // No sky to be had: its colours as a gradient, the studio's room for light.
      if (definition.sky.seen) {
        background = gradientSky(sky.horizon ?? '#bdc1ca', sky.zenith ?? '#8fa6c2');
        scene.background = background;
      }
      if (renderer) scene.environment = roomEnvironment(renderer);
      // The models are lit by the room now, not the dark sky that was measured (ADR-100).
      skyFellBack = true;
    };
    const onSky = (sky: SkyParts | undefined): void => {
      if (!sky) return fallbackSky();
      const started = performance.now();
      // The light first: while it is made, the generator's working target and
      // the half-float copy (10.5 MB) stand beside it, and the world's files
      // wait for it (WorldLoader.requestFirst). Made before the sky's cube
      // goes up, that moment stays below the world's steady count
      // (worlds/budget.ts, moments).
      if (renderer) {
        const pmrem = new THREE.PMREMGenerator(renderer);
        const source = lightTexture(sky.light);
        environment?.dispose();
        environment = pmrem.fromEquirectangular(source);
        source.dispose();
        pmrem.dispose();
        scene.environment = environment.texture;
      }
      if (sky.faces && definition.sky.seen) {
        background?.dispose();
        // Its own cube, never the 2D image three would turn into a larger one (sky.ts).
        background = cubeTexture(sky.faces);
        if (renderer) {
          renderer.initTexture(background);
          // Up now: the page need not keep the faces' bytes (as a 360° photo's, pano.ts).
          for (const face of background.image as THREE.DataTexture[]) {
            face.image = {
              data: new Uint8Array(0),
              width: face.image.width,
              height: face.image.height,
            };
          }
        }
        scene.background = background;
      }
      target.redrawShadow();
      if (renderer) {
        console.info(`world sky: up in ${Math.round(performance.now() - started)} ms on the page`);
      }
    };

    const maxFace = renderer?.capabilities.maxCubemapSize ?? Infinity;
    world = buildWorld(definition, {
      ...(options.fetchers ? { fetchers: options.fetchers } : {}),
      ...(options.manifest ? { manifest: options.manifest } : {}),
      ...(options.urlOf ? { urlOf: options.urlOf } : {}),
      prepareSky: (image) =>
        skyInWorker({
          image,
          exposure: definition.sky.exposure,
          maxFace,
          seen: definition.sky.seen,
        }),
      onSky,
    });
    scene.add(world.root);
    if (!isAr) modelLight = new ModelLight();
    const yaw = THREE.MathUtils.degToRad(definition.sky.yaw);
    scene.backgroundRotation.set(0, yaw, 0);
    scene.environmentRotation.set(0, yaw, 0);
    scene.environmentIntensity = definition.sky.environment;
    if (!definition.sky.seen) scene.background = new THREE.Color(definition.background ?? 0x808080);
    if (definition.fog) {
      const colour =
        definition.fog.colour !== undefined
          ? new THREE.Color(definition.fog.colour)
          : fogColour(sky.horizon ?? '#bdc1ca');
      scene.fog = new THREE.FogExp2(colour, definition.fog.density);
    }
    if (renderer) renderer.toneMappingExposure = definition.sky.exposure;
    target.camera.far = WORLD_FAR;
    target.camera.updateProjectionMatrix();
    // Glow in daylight: brighter, or it reads as paint (the spec's 1.6 outdoors).
    built.root.traverse((node) => {
      const mesh = node as THREE.Mesh<THREE.BufferGeometry, THREE.Material | THREE.Material[]>;
      if (!mesh.material) return;
      for (const material of Array.isArray(mesh.material) ? mesh.material : [mesh.material]) {
        if (
          material instanceof THREE.MeshStandardMaterial &&
          material.userData['finish'] === 'glow'
        ) {
          material.emissiveIntensity = definition.glow;
        }
      }
    });
  }

  // A 360° photo (ADR-036): VR only; `?world=` has already made it a world.
  let pano: PanoView | undefined;
  if (options.pano && !isAr && lesson.scene.type === 'pano360') {
    const asked = options.pano;
    const studio = buildStudio({ flat });
    pano = new PanoView({
      lesson,
      media: asked.media,
      ...(asked.variants ? { variants: asked.variants } : {}),
      // Without a renderer no photo shows at all (the studio), so no caps either.
      ...(renderer ? { gpu: rendererGpu(renderer), caps: rendererCaps(renderer) } : {}),
      scene,
      prepare:
        asked.prepare ??
        ((source) =>
          preparePano(source, { maxFace: renderer?.capabilities.maxCubemapSize ?? 4096 })),
      flat,
      studio: {
        root: studio.root,
        lights: studio.lights,
        ...(renderer && !flat ? { environment: () => roomEnvironment(renderer) } : {}),
        environmentIntensity: STUDIO_ENVIRONMENT,
      },
      redrawShadow: () => target.redrawShadow(),
      ...(asked.onNote ? { onNote: asked.onNote } : {}),
    });
    scene.add(pano.root);
  }

  const ready = world
    ? world.loader.ready().then(() => target.redrawShadow())
    : (pano?.ready ?? Promise.resolve());
  target.redrawShadow();

  // The sky as measured, once: the platform's sky by its import, a package's own by the worker. Only
  // for a world that was built (never in the flat look, whose sky need not be in the manifest).
  const radiance =
    world && definition ? skyRadiance(hdriFile(definition.sky.hdri, options.manifest)) : undefined;
  // One object, filled in every frame.
  const light: { sun: number; environment: number; sky: number | undefined; exposure: number } = {
    sun: 0,
    environment: 0,
    sky: undefined,
    exposure: 1,
  };
  const worldLight = (): WorldLight | undefined => {
    if (!world || !definition) return undefined;
    light.sun = definition.sun.intensity * world.builder.light.sun;
    light.environment = definition.sky.environment * world.builder.light.environment;
    light.sky = skyFellBack ? ROOM_RADIANCE : radiance;
    light.exposure = definition.sky.exposure;
    return light;
  };
  const eye = new THREE.Vector3();

  return {
    ready,
    ...(world ? { world } : {}),
    ...(pano ? { pano } : {}),
    ...(modelLight ? { modelLight } : {}),
    worldLight,
    missing: () => world?.loader.missing ?? 0,
    atStep() {
      world?.loader.applyLate();
      target.redrawShadow();
    },
    update(dt, elapsed, moving, held = false, black = false) {
      const camera = target.activeCamera();
      world?.update(dt, elapsed, camera, held);
      if (world && definition) {
        scene.environmentIntensity = definition.sky.environment * world.builder.light.environment;
      }
      const now = modelLight ? worldLight() : undefined;
      if (modelLight && now)
        modelLight.update(dt, modelLightNeed(now), camera.getWorldPosition(eye), black);
      halos?.update(camera, elapsed);
      if (definition && definition.sky.drift !== 0) {
        scene.backgroundRotation.y += THREE.MathUtils.degToRad(definition.sky.drift / 60) * dt;
      }
      if (moving) target.redrawShadow();
    },
    setWorldStates(states, animate) {
      world?.setStates(states, animate);
    },
    dispose() {
      halos?.dispose();
      modelLight?.dispose();
      world?.dispose();
      pano?.dispose();
      environment?.dispose();
      background?.dispose();
    },
  };
}
